import React, { useMemo, useState } from 'react';
import Modal from '@amzn/awsui-components-console/modal';
import Box from '@amzn/awsui-components-console/box';
import Button from '@amzn/awsui-components-console/button';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import FormField from '@amzn/awsui-components-console/form-field';
import Input from '@amzn/awsui-components-console/input';
import Textarea from '@amzn/awsui-components-console/textarea';
import Multiselect from '@amzn/awsui-components-console/multiselect';
import Select from '@amzn/awsui-components-console/select';
import Alert from '@amzn/awsui-components-console/alert';
import Form from '@amzn/awsui-components-console/form';
import Spinner from '@amzn/awsui-components-console/spinner';

import type {
  MultiselectProps,
  SelectProps,
} from '@amzn/awsui-components-console';
import type { CreateStringGroupModalProps } from '../types';
import { SG_NAME_MAX_LENGTH, SG_DESCRIPTION_MAX_LENGTH } from '../constants';
import {
  getNameError,
  getDescriptionError,
  getTeamNameError,
  getSourceLocaleError,
  getCxFamilyError,
  getCxIdsError,
} from './createStringGroupValidation';
import { useCreateStringGroupForm } from '../hooks/useCreateStringGroupForm';
import {
  useListCxFamiliesQuery,
  useDescribeCxFamilyQuery,
} from 'src/generated/graphql';

/** Visual indicator for required form fields. */
const RequiredAsterisk: React.FC = () => (
  <span style={{ color: '#d91515' }}>*</span>
);

/** Modal form for creating a new string group within a namespace. */
const CreateStringGroupModal: React.FC<CreateStringGroupModalProps> = ({
  visible,
  onDismiss,
  onSubmit,
  cxFamily,
}) => {
  const form = useCreateStringGroupForm();

  // ─── CX Family state (only used when namespace has no default) ──────────
  const [selectedFamilyOption, setSelectedFamilyOption] =
    useState<SelectProps.Option | null>(null);

  // Determine the effective cxFamilyId: either from namespace default or user selection
  const effectiveCxFamilyId = cxFamily || selectedFamilyOption?.value || '';

  // ─── GraphQL queries ────────────────────────────────────────────────────
  // List all families (only needed when namespace has no default)
  const { data: listFamiliesData, loading: listFamiliesLoading } =
    useListCxFamiliesQuery({
      skip: !!cxFamily || !visible,
    });

  // Describe the selected/default family to get CxIds and sourceLocales
  const { data: describeFamilyData, loading: describeFamilyLoading } =
    useDescribeCxFamilyQuery({
      variables: { cxFamilyId: effectiveCxFamilyId },
      skip: !effectiveCxFamilyId || !visible,
    });

  // ─── Derived options from describeCxFamily response ─────────────────────
  const cxIdOptions: MultiselectProps.Option[] = useMemo(() => {
    const cxIds = describeFamilyData?.describeCxFamily?.cxFamily?.cxIds;
    if (!cxIds) return [];
    return cxIds.map((item) => ({
      label: item.cxId,
      value: item.cxId,
    }));
  }, [describeFamilyData]);

  const sourceLocaleOptions: SelectProps.Option[] = useMemo(() => {
    const familyDetail = describeFamilyData?.describeCxFamily?.cxFamily;
    if (!familyDetail) return [];

    // Step 1: Intersection of sourceLocales across all selected CX IDs
    const selectedCxIdValues = new Set(
      form.selectedCxIds.map((opt) => opt.value),
    );
    const selectedCxIdDetails =
      familyDetail.cxIds?.filter((cxIdDetail) =>
        selectedCxIdValues.has(cxIdDetail.cxId),
      ) ?? [];

    let cxIdIntersection: Set<string>;
    if (selectedCxIdDetails.length === 0) {
      cxIdIntersection = new Set<string>();
    } else {
      // Start with the first selected CxId's sourceLocales
      cxIdIntersection = new Set(selectedCxIdDetails[0].sourceLocales ?? []);
      // Intersect with each subsequent selected CxId
      for (let i = 1; i < selectedCxIdDetails.length; i++) {
        const locales = new Set(selectedCxIdDetails[i].sourceLocales ?? []);
        cxIdIntersection.forEach((locale) => {
          if (!locales.has(locale)) {
            cxIdIntersection.delete(locale);
          }
        });
      }
    }

    // Step 2: Union the intersection with family-level sourceLocales
    const resultSet = new Set<string>(cxIdIntersection);
    familyDetail.sourceLocales?.forEach((locale) => resultSet.add(locale));

    return Array.from(resultSet)
      .sort()
      .map((locale) => ({ label: locale, value: locale }));
  }, [describeFamilyData, form.selectedCxIds]);

  // ─── Family dropdown options (when no default) ──────────────────────────
  const familySelectOptions: SelectProps.Option[] = useMemo(() => {
    const families = listFamiliesData?.listCxFamilies?.cxFamilies;
    if (!families) return [];
    return families.map((f) => ({
      label: f.cxFamilyId,
      value: f.cxFamilyId,
    }));
  }, [listFamiliesData]);

  // ─── Handlers ───────────────────────────────────────────────────────────
  const handleFamilyChange = ({
    detail,
  }: {
    detail: SelectProps.ChangeDetail;
  }) => {
    setSelectedFamilyOption(detail.selectedOption);
    // Reset dependent fields when family changes
    form.setSelectedCxIds([]);
    form.setSourceLocale(null);
  };

  const handleDismiss = () => {
    if (!form.submitting) {
      form.resetForm();
      setSelectedFamilyOption(null);
      onDismiss();
    }
  };

  const handleSubmitClick = () => {
    form.handleSubmit(
      onSubmit,
      () => {
        setSelectedFamilyOption(null);
        onDismiss();
      },
      effectiveCxFamilyId,
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleDismiss}
      size="large"
      header="Create string group"
      closeAriaLabel="Close create string group modal"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              variant="link"
              onClick={handleDismiss}
              disabled={form.submitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmitClick}
              loading={form.submitting}
            >
              Create string group
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <Form>
        <SpaceBetween size="l">
          {form.submitError && (
            <Alert
              type="error"
              dismissible
              dismissAriaLabel="Dismiss error"
              onDismiss={() => form.setSubmitError(null)}
            >
              {form.submitError}
            </Alert>
          )}

          <FormField
            label={
              <>
                Name <RequiredAsterisk />
              </>
            }
            constraintText={`Maximum ${SG_NAME_MAX_LENGTH} characters.`}
            errorText={getNameError(form.name, form.showValidation)}
          >
            <Input
              value={form.name}
              placeholder="Homepage_Banners, Checkout_Flow"
              onChange={({ detail }) => form.setName(detail.value)}
              disabled={form.submitting}
            />
          </FormField>

          {cxFamily ? (
            <FormField
              label={
                <>
                  CX family <RequiredAsterisk />
                </>
              }
              constraintText="Inherited from the namespace. Not editable."
            >
              <Input value={cxFamily} readOnly />
            </FormField>
          ) : (
            <FormField
              label={
                <>
                  CX family <RequiredAsterisk />
                </>
              }
              constraintText="Select a CX family for this string group."
              errorText={getCxFamilyError(
                selectedFamilyOption?.value || '',
                form.showValidation,
              )}
            >
              <Select
                selectedOption={selectedFamilyOption}
                placeholder="Choose a CX family"
                options={familySelectOptions}
                onChange={handleFamilyChange}
                disabled={form.submitting}
                loadingText="Loading CX families"
                statusType={listFamiliesLoading ? 'loading' : 'finished'}
                filteringType="auto"
                filteringPlaceholder="Find CX family"
              />
            </FormField>
          )}

          <FormField
            label={
              <>
                Description <RequiredAsterisk />
              </>
            }
            constraintText={`Maximum ${SG_DESCRIPTION_MAX_LENGTH} characters.`}
            errorText={getDescriptionError(
              form.description,
              form.showValidation,
            )}
            stretch
          >
            <Textarea
              value={form.description}
              placeholder="Describe the purpose of this string group."
              rows={3}
              onChange={({ detail }) => form.setDescription(detail.value)}
              disabled={form.submitting}
            />
          </FormField>

          <FormField
            label={
              <>
                Team name <RequiredAsterisk />
              </>
            }
            constraintText="The team responsible for managing this string group."
            errorText={getTeamNameError(form.teamName, form.showValidation)}
          >
            <Input
              value={form.teamName}
              placeholder="Retail Frontend Team"
              onChange={({ detail }) => form.setTeamName(detail.value)}
              disabled={form.submitting}
            />
          </FormField>

          <FormField
            label={
              <>
                CX IDs <RequiredAsterisk />
              </>
            }
            constraintText="Select the customer experience IDs for this string group. Locales are determined by the selected CX IDs."
            errorText={getCxIdsError(
              form.selectedCxIds.length,
              form.showValidation,
            )}
          >
            {describeFamilyLoading ? (
              <span role="status" aria-label="Loading CX IDs">
                <Spinner />
              </span>
            ) : (
              <Multiselect
                selectedOptions={form.selectedCxIds}
                placeholder="Choose CX IDs"
                filteringType="auto"
                filteringPlaceholder="Find CX ID"
                options={cxIdOptions}
                onChange={({ detail }) => {
                  form.setSelectedCxIds([...detail.selectedOptions]);
                  form.setSourceLocale(null);
                }}
                disabled={
                  form.submitting ||
                  describeFamilyLoading ||
                  !effectiveCxFamilyId
                }
              />
            )}
          </FormField>

          <FormField
            label={
              <>
                Source locale <RequiredAsterisk />
              </>
            }
            constraintText={
              form.selectedCxIds.length === 0
                ? 'Select CX IDs first, then choose a source locale'
                : 'Select one source locale for your strings'
            }
            errorText={getSourceLocaleError(
              form.sourceLocale,
              form.showValidation,
            )}
          >
            {describeFamilyLoading ? (
              <span role="status" aria-label="Loading source locales">
                <Spinner />
              </span>
            ) : (
              <Select
                selectedOption={form.sourceLocale}
                placeholder="Choose a source locale"
                options={sourceLocaleOptions}
                onChange={({ detail }) =>
                  form.setSourceLocale(detail.selectedOption)
                }
                disabled={
                  form.submitting ||
                  describeFamilyLoading ||
                  !effectiveCxFamilyId ||
                  form.selectedCxIds.length === 0
                }
              />
            )}
          </FormField>
        </SpaceBetween>
      </Form>
    </Modal>
  );
};

export default CreateStringGroupModal;
